import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/api`;

export interface Question {
  id: string;
  text: string;
  options: Array<{
    id: string;
    text: string;
    order: number;
  }>;
  difficulty: string;
}

export interface GameSession {
  id: string;
  current_score: number;
  total_time: number;
}

export interface LeaderboardEntry {
  id: string;
  name: string | null;
  score: number;
  time: string;
  questions_answered: number;
}

export interface Sponsor {
  id: string;
  name: string;
  logo_url: string;
  website_url: string;
}

export const api = {
  // Game Session endpoints
  startGame: async (signal?: AbortSignal): Promise<GameSession> => {
    const response = await axios.post(`${API_BASE_URL}/game/start/`, null, { signal });
    return response.data;
  },

  endGame: async (gameSessionId: string, signal?: AbortSignal): Promise<GameSession> => {
    const response = await axios.delete(`${API_BASE_URL}/game/end/${gameSessionId}/`, { signal });
    return response.data;
  },

  getGameSession: async (gameSessionId: string, signal?: AbortSignal): Promise<GameSession> => {
    const response = await axios.get(`${API_BASE_URL}/game/session/${gameSessionId}/`, { signal });
    return response.data;
  },

  // Question endpoints
  getQuestion: async (gameSessionId: string, signal?: AbortSignal): Promise<Question> => {
    const response = await axios.get(`${API_BASE_URL}/question/`, {
      params: { game_session_id: gameSessionId },
      signal
    });
    return response.data;
  },

  submitAnswer: async (
    questionId: string,
    selectedOptionId: string,
    gameSessionId: string,
    usedLifelines: string[],
    signal?: AbortSignal
  ) => {
    const response = await axios.post(`${API_BASE_URL}/question/`, {
      question_id: questionId,
      selected_option_id: selectedOptionId,
      game_session_id: gameSessionId,
      used_lifelines: usedLifelines,
    }, { signal });
    return response.data;
  },

  // Lifeline endpoints
  use5050: async (questionId: string, gameSessionId: string, signal?: AbortSignal) => {
    const response = await axios.post(`${API_BASE_URL}/lifeline/`, {
      question_id: questionId,
      game_session_id: gameSessionId,
      lifeline: '50/50',
    }, { signal });
    return response.data;
  },

  useTip: async (questionId: string, gameSessionId: string, signal?: AbortSignal) => {
    const response = await axios.post(`${API_BASE_URL}/lifeline/`, {
      question_id: questionId,
      game_session_id: gameSessionId,
      lifeline: 'tip',
    }, { signal });
    return response.data;
  },

  // Leaderboard endpoints
  getLeaderboard: async (signal?: AbortSignal): Promise<LeaderboardEntry[]> => {
    const response = await axios.get(`${API_BASE_URL}/leaderboard/`, { signal });
    return response.data;
  },

  // Sponsor endpoints
  getSponsors: async (signal?: AbortSignal): Promise<Sponsor[]> => {
    const response = await axios.get(`${API_BASE_URL}/sponsors/`, { signal });
    return response.data;
  },

  submitScore: async (
    name: string | null, 
    score: number, 
    time: string, 
    gameSessionId: string,
    signal?: AbortSignal
  ) => {
    const response = await axios.post(`${API_BASE_URL}/leaderboard/`, {
      name,
      score,
      time,
      game_session: gameSessionId,
    }, { signal });
    return response.data;
  }
}; 